/* Today's Activity — a 4-tile strip at the very top of the Dashboard,
 * directly under the "Brag a Little" pill.
 *
 * Designed for a real-estate-wholesaler's mental model: the four numbers
 * that answer "should I be worried right now?" in one glance:
 *
 *   New Leads today  ·  Follow-ups Due  ·  Contracts Pending  ·  Closings This Week
 *
 * All four numbers come from a single field on the dashboard summary
 * response (`today_activity`) so we're not adding a round-trip. When
 * data isn't loaded yet each tile renders a subtle placeholder instead
 * of jumping numbers.
 */
import { Link } from "react-router-dom";
import { Flame, PhoneCall, FileSignature, Target } from "lucide-react";

const TILES = [
  {
    key: "new_leads",
    icon: Flame,
    label: "New Leads",
    accent: "#F97316",             // orange — heat / new / momentum
    tint: "rgba(249, 115, 22, 0.10)",
    to: "/v3/crm/leads?filter=new",
    testId: "todays-activity-new-leads",
  },
  {
    key: "follow_ups_due",
    icon: PhoneCall,
    label: "Follow-ups Due",
    accent: "#0EA5E9",             // blue — action / call
    tint: "rgba(14, 165, 233, 0.10)",
    to: "/v3/crm/leads?filter=follow-up",
    testId: "todays-activity-follow-ups",
  },
  {
    key: "contracts_pending",
    icon: FileSignature,
    label: "Contracts Pending",
    accent: "#10B981",             // emerald — money / green light
    tint: "rgba(16, 185, 129, 0.10)",
    to: "/v3/crm/pipeline?stage=Under+Contract",
    testId: "todays-activity-contracts-pending",
  },
  {
    key: "closings_this_week",
    icon: Target,
    label: "Closing This Week",
    accent: "#F59E0B",             // brand gold — the win state
    tint: "rgba(230, 167, 62, 0.12)",
    to: "/v3/crm/closings",
    testId: "todays-activity-closings",
  },
];

export default function TodaysActivityStrip({ activity }) {
  return (
    <div className="mb-6" data-testid="todays-activity-strip">
      <div className="label-eyebrow flex items-center gap-2 mb-2 text-[var(--ink-2)]">
        Today&apos;s Activity
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TILES.map((t) => {
          const value = activity?.[t.key];
          const displayValue = value === undefined || value === null ? "—" : value;
          const Icon = t.icon;
          return (
            <Link
              key={t.key}
              to={t.to}
              className="group relative overflow-hidden rounded-xl bg-white border border-[var(--line)] p-4 transition-all hover:shadow-md hover:-translate-y-0.5"
              data-testid={t.testId}
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: t.tint }}
                aria-hidden="true"
              />
              <div className="relative flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: t.tint, color: t.accent }}
                >
                  <Icon size={20} strokeWidth={2.25} />
                </div>
                <div className="min-w-0">
                  <div
                    className="font-display font-black text-2xl leading-none tabular-nums"
                    style={{ color: t.accent }}
                  >
                    {displayValue}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] font-semibold mt-1 truncate">
                    {t.label}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
